import { ArrowLeft } from '@mui/icons-material';
import { IconButton, Skeleton } from '@mui/material';
import { includes, isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { MarketViewApplication } from '../../Generated/Mark-It-View-Api';
import {RootState, useApplicationSelector } from '../../Redux/ReduxStore';
import CommodityManagementSiteSelector from '../MainContainer/CommodityManagement/CommodityManagementSiteSelector';
import { INavProps } from './InterfaceNav';
import './SidebarNavigation.scss';
import { GetCustomerDataResponse, TierModel, LocationModel } from '../../Generated/Commodity-Management-Api';
import { useLoadingState } from '../../Hooks';
import { CommodityManagementApi } from '../../Apis/Apis';

interface NavigationLink {
  id: string;
  type: 'Link';
  url: string;
  label: string;
  end?: boolean;
  subMenuItems?: NavigationLink[];
  showCondition?: () => boolean;
}

interface NavigationHeader {
  id:string;
  type: 'Header';
  headerName: string;
  subMenuItems: NavigationLink[];
  showCondition?: () => boolean;
}

interface NavigationHorizontalBreak {
  id: string;
  type: 'HorizontalBreak';
}

type NavigationItem = NavigationLink | NavigationHeader | NavigationHorizontalBreak;

export const SidebarNavigation: React.FC<INavProps> = () => {
  const access = useApplicationSelector((state: RootState) => state.userAccess.hasAccess);
  const userAccess = useApplicationSelector((state: RootState) => state.userAccess);

  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);
  const [collapsedItems, setCollapsedItems] = useState<Record<string, boolean>>({});

  const userHasCommodityManagementSite = (): boolean => userAccess.commodityManagementUserSiteAccesses?.length > 0;
  const userHasApplication = (application: MarketViewApplication): boolean => includes(userAccess.marketViewApplications, application);
  const userIsAdmin = (): boolean => userAccess.isAdmin;

  const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse | undefined => state.commodityManagement.customerData, isEqual);

  const toggleCollapse = (id: string): void => {
    setCollapsedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    const savedCollapsedItems = localStorage.getItem('navBarCollapsedItems');
    if (savedCollapsedItems) {
      setCollapsedItems(JSON.parse(savedCollapsedItems));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('navBarCollapsedItems', JSON.stringify(collapsedItems));
  }, [collapsedItems]);

  useEffect(() => {
    const getDashboardMenuItems = (): NavigationLink[] => {

      // If customerData is undefined, it's loading.
      if (customerData === undefined) {
        return [];
      }

      // If customerData is loaded but empty or invalid, return nothing.
      const { tiers, locations } = customerData;
      if (!tiers?.length || !locations?.length) {
        return [];
      }

      const uniqueRanksArray = Array.from(new Set(tiers.map(t => t.tierRank)));
      const sortedRanks = uniqueRanksArray.sort((a, b) => b - a);
      const secondHighestRank = sortedRanks[1];

      if (secondHighestRank === undefined) {
        return [];
      }

      const secondTier = tiers.find(t => t.tierRank === secondHighestRank);
      if (!secondTier) {
        return [];
      }

      const secondTierIds = tiers
        .filter(t => t.tierRank === secondHighestRank)
        .map(t => t.tierId);
      const filteredLocations = locations.filter(loc => secondTierIds.includes(Number(loc.tierId)));

      if (filteredLocations.length === 0) {
        return [];
      }

      return [{
        id: 'Commodity-Management-Second-Tier-Locations',
        type: 'Link',
        url: '/commodity-management/positions',
        label: secondTier.tierName,
        subMenuItems: filteredLocations.map((loc: LocationModel) => ({
          id: `Commodity-Management-Dashboard-${loc.locationId}`,
          type: 'Link',
          url: `/commodity-management/dashboard/${loc.locationId}`,
          label: loc.locationName,
        })),
      }];
    };

    const items: NavigationItem[] = [
      {
        id: 'Commodity-Management-Header',
        type: 'Header',
        headerName: 'Commodity Management',
        showCondition: userHasCommodityManagementSite,
        subMenuItems: [
          { id: 'Commodity-Management-Reports', type: 'Link', url: '/commodity-management/reports', label: 'Reports' },
          {
            id: 'Commodity-Management-Budgets',
            type: 'Link',
            url: '/commodity-management/budgets',
            label: 'Budgets',
            end: true,
            subMenuItems: [
              { id: 'Commodity-Management-Update-Budgets', type: 'Link', url: '/commodity-management/budgets/add', label: 'Add Budgets', end: true },
            ],
          },
          {
            id: 'Commodity-Management-Positions',
            type: 'Link',
            url: '/commodity-management/positions',
            label: 'Positions',
            end: true,
            subMenuItems: [
              { id: 'Commodity-Management-Update-Positions', type: 'Link', url: '/commodity-management/positions/add', label: 'Add Positions', end: true },
            ],
          },
          //{ id: 'Commodity-Management-Allocations', type: 'Link', url: '/commodity-management/allocations', label: 'Allocations' },
          ...getDashboardMenuItems(),
          { id: 'Commodity-Management-Notes', type: 'Link', url: '/commodity-management/notes', label: 'Notes' },
          { id: 'Commodity-Management-Settings', type: 'Link', url: '/commodity-management/settings', label: 'Settings' },
        ],
      },
      {
        id: 'Tools-Header',
        type: 'Header',
        headerName: 'Tools',
        subMenuItems: [
          {
            id: 'Tools-Mark-It-View',
            type: 'Link',
            url: '/tools/market-view',
            label: 'Mark-It View',
            showCondition: (): boolean => userHasApplication(MarketViewApplication.MarketView),
          },
          {
            id: 'Tools-Matrix',
            type: 'Link',
            url: '/tools/matrix',
            label: 'Matrix',
            showCondition: (): boolean => userHasApplication(MarketViewApplication.Deciles),
          },
          {
            id: 'Tools-Seasonal-Matrix',
            type: 'Link',
            url: '/tools/seasonal-matrix',
            label: 'Seasonal Matrix',
            showCondition: (): boolean => userHasApplication(MarketViewApplication.SeasonalMatrix),
          },
          {
            id: 'Tools-Forecast',
            type: 'Link',
            url: '/tools/forecast',
            label: 'Forecast',
            showCondition: (): boolean => userHasApplication(MarketViewApplication.Forecast),
          },
          {
            id: 'Tools-Hedge-Plans',
            type: 'Link',
            url: '/tools/hedge-plans',
            label: 'Hedge Plans',
            showCondition: (): boolean => userHasApplication(MarketViewApplication.HedgePlan),
          },
          {
            id: 'Tools-Eia-Gas-Storage',
            type: 'Link',
            url: '/tools/eia-gas-storage',
            label: 'EIA Gas Storage',
            showCondition: (): boolean => userIsAdmin() || userHasApplication(MarketViewApplication.EiaNaturalGasStorage),
          },
        ],
      },
      {
        id: 'Administration-Header',
        type: 'Header',
        headerName: 'Administration',
        showCondition: userIsAdmin,
        subMenuItems: [
          { id: 'Administration-Sites', type: 'Link', url: '/admin/sites', label: 'Sites' },
          { id: 'Administration-Users', type: 'Link', url: '/admin/users', label: 'Users' },
          { id: 'Administration-Companies', type: 'Link', url: '/admin/companies', label: 'Companies' },
          { id: 'Administration-Commodities', type: 'Link', url: '/admin/commodities', label: 'Commodities' },
          { id: 'Administration-Cache-Management', type: 'Link', url: '/admin/cache-management', label: 'Cache Management' },
        ],
      },
    ];

    setNavigationItems(items);
  }, [userAccess, customerData]);

  const renderNavigationItem = (item: NavigationItem): React.ReactNode => {
    if (item.type === 'Link') {
      const { id, url, label, end, subMenuItems, showCondition } = item as NavigationLink;

      if (showCondition && !showCondition()) return null;

      const isCollapsed = collapsedItems[id] || false;

      return (
        <React.Fragment key={id}>
          <li className="nav-item">
            <NavLink
              end={end}
              className={(props) => (props.isActive ? 'nav-link active' : 'nav-link')}
              to={url}
            >
              <span className="aside-text">{label}</span>
            </NavLink>
            {subMenuItems && (
              <IconButton onClick={() => toggleCollapse(id)} style={{ marginLeft: '8px' }} size="small">
                <ArrowLeft
                  style={{
                    transform: isCollapsed ? 'rotate(0deg)' : 'rotate(-90deg)',
                  }}
                />
              </IconButton>
            )}
          </li>
          {!isCollapsed && subMenuItems && (
            <ul className="sub-menu">{subMenuItems.map(renderNavigationItem)}</ul>
          )}
        </React.Fragment>
      );
    }

    if (item.type === 'Header') {
      const { id, headerName, subMenuItems, showCondition } = item as NavigationHeader;

      if (showCondition && !showCondition()) return null;

      const visibleSubMenuItems = subMenuItems.filter(
        (subItem) => !subItem.showCondition || subItem.showCondition()
      );

      if (visibleSubMenuItems.length === 0) return null;

      const isCollapsed = collapsedItems[id] || false;

      return (
        <div key={id} className="header-container">
          <li className="nav-header-item" >
            <span className="nav-header">{headerName}</span>
            <IconButton onClick={() => toggleCollapse(id)} size="small">
              <ArrowLeft
                style={{
                  transform: isCollapsed ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}
              />
            </IconButton>
          </li>
          {!isCollapsed && (
            <div className="header-sub-menu">
              {visibleSubMenuItems.map(renderNavigationItem)}
              <hr />
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'HorizontalBreak') {
      return <hr key={item.id} />;
    }

    return null;
  };

  if (!access || navigationItems.length <= 1) {
    return null;
  }

  return (
    <aside id="menu">
      <ul>
        <div className="site-selector">
          <CommodityManagementSiteSelector />
        </div>
        {navigationItems.map(renderNavigationItem)}
      </ul>
    </aside>
  );
};

export default SidebarNavigation;